import {
  getPinyinData,
  getPinyinSyllableEntry,
  getPinyinSyllableTermOptions,
} from "@-/dev.improve.haniverse.assets.pinyin";
import { tonelessSyllableForPinyinAssetLookup } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";

const BRACKET_CONTENT_REGEX = /\[([^\]]+)\]/;

/**
 * The English approximation of a syllable's sound, sourced from that syllable's `term` in
 * `pinyin.toml`. The bracketed span is the phonetically relevant part (e.g. `[gun]` → `gun`,
 * `[bun]ny` → `bun` within `bunny`); the pronunciation panel shows the whole term in italic
 * under the reading, with that span also bold (_**bun**ny_).
 */
export type PinyinSoundHint = {
  /** `term` with bracket characters removed (`[bun]ny` → `bunny`). */
  displayTerm: string;
  /** Letters inside the first `[…]` of `term` — the span to bold. */
  phoneticSpan: string;
  /** First curated `term` from `pinyin.toml`, still carrying its `[…]` phonetic brackets. */
  term: string;
  /** Toneless syllable key (`gan`, `san`). */
  tonelessSyllable: string;
};

/** Pull the first `[…]` phonetic span out of a `pinyin.toml` term, or `null` when none. */
export function getTermPhoneticSpan(term: string): string | null {
  return BRACKET_CONTENT_REGEX.exec(term)?.[1] ?? null;
}

/**
 * Resolve the curated English sound approximation for a reading (tone-number or toneless).
 * Returns `null` when the syllable has no done `pinyin.toml` entry or no bracketed `term`.
 */
export function resolvePinyinSoundHint(pinyin: string): PinyinSoundHint | null {
  const tonelessSyllable = tonelessSyllableForPinyinAssetLookup(pinyin);
  if (tonelessSyllable.length === 0) {
    return null;
  }
  const entry = getPinyinSyllableEntry(getPinyinData(), tonelessSyllable);
  if (entry === undefined) {
    return null;
  }
  const term = getPinyinSyllableTermOptions(entry)[0];
  if (term === undefined) {
    return null;
  }
  const phoneticSpan = getTermPhoneticSpan(term);
  if (phoneticSpan === null || phoneticSpan.length === 0) {
    return null;
  }
  return {
    displayTerm: term.replaceAll(/[[\]]/gu, ""),
    phoneticSpan,
    term,
    tonelessSyllable,
  };
}
